import { Action, createAction, Store } from '@reduxjs/toolkit';
import { app } from 'store';
import { DisplaySettings } from 'types';
import {
  beginDisplaySettingsLoad,
  endDisplaySettingsLoad,
  receiveDisplaySettings,
  settleDisplaySettingsSave,
  type SaveEffect,
} from './displaySettingsSave';
import { applyLoadedDisplaySettings } from './restoreDisplaySettingsInStore';

export const DISPLAY_SETTINGS_SAVE_ERROR = "Couldn't save display settings. Please try again.";

interface DisplaySettingsResponse {
  data: Partial<DisplaySettings>;
}

interface DisplaySettingsSaveAction extends Action {
  meta: {
    onSuccess: (response: unknown, action: Action, store: Store) => void;
    onError: (error: { response?: { status?: number } }, action: Action, store: Store) => void;
  };
}

function followUp(effect: SaveEffect, store: Store): void {
  if (effect.type === 'patch') {
    store.dispatch(saveDisplaySettingsRequest(effect.fields, effect.generation));
  }
}

/** One PATCH per changed field. A failed save toasts and leaves the toggle where the user put it. */
export const saveDisplaySettingsRequest = createAction(
  'request/saveDisplaySettings',
  (fields: Partial<DisplaySettings>, generation: number) => ({
    payload: {
      request: {
        url: '/users/me/preferences',
        method: 'PATCH',
        data: fields,
      },
    },
    meta: {
      generation,
      onSuccess: (_response: unknown, _action: Action, store: Store) => {
        const result = settleDisplaySettingsSave(generation, true);
        if (!result.accepted || !result.saved) return {};
        store.dispatch(app.actions.setSavedDisplaySettings(result.saved));
        followUp(result.effect, store);
        return {};
      },
      onError: (_error: { response?: { status?: number } }, _action: Action, store: Store) => {
        const result = settleDisplaySettingsSave(generation, false);
        if (result.accepted && result.saved) {
          store.dispatch(app.actions.setSavedDisplaySettings(result.saved));
        }
        store.dispatch(app.actions.setSnackMessage({ message: DISPLAY_SETTINGS_SAVE_ERROR, type: 'error' }));
        throw new Error(DISPLAY_SETTINGS_SAVE_ERROR);
      },
    },
  }),
);

/** Loads the saved copy and applies it to the viewer. A failure is not retried and does not block later writes. */
export const getDisplaySettings = createAction('request/getDisplaySettings', () => {
  beginDisplaySettingsLoad();
  return {
    payload: {
      request: {
        url: '/users/me/preferences',
        method: 'GET',
      },
    },
    meta: {
      onSuccess: (response: DisplaySettingsResponse, _action: Action, store: Store) => {
        const saved = receiveDisplaySettings(response.data);
        store.dispatch(app.actions.setSavedDisplaySettings(saved));
        applyLoadedDisplaySettings(store, saved);
        return response;
      },
      onError: (error: unknown, _action: Action, _store: Store) => {
        endDisplaySettingsLoad();
        throw error;
      },
      onAbort: () => {
        endDisplaySettingsLoad();
      },
    },
  };
});

export type { DisplaySettingsSaveAction };
